import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, Component, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { EnvironmentsService, IOcbLocalAgent, IOcbLocalAgentCommand, LocalAgentsService, OcbLocalAgentCommand, OcbLocalAgentCommandStatus, OcbLocalAgentCommandTarget, UserService } from '@app/core';
import { TranslateService } from '@ngx-translate/core';
import { DxDataGridComponent } from 'devextreme-angular/ui/data-grid';
import DataSource from 'devextreme/data/data_source';
import notify from 'devextreme/ui/notify';
import { forkJoin } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { LocalAgentCommandComponent } from '../../shared';
import { DxSelectBoxComponent } from 'devextreme-angular';

const now = new Date();
const initialDates: [Date, Date] = [new Date(now), new Date(now)];

@Component({
  selector: 'app-local-agents-commands',
  templateUrl: './local-agents-commands.component.html',
  styleUrls: ['./local-agents-commands.component.scss'],
})
export class LocalAgentsCommandsComponent implements AfterViewInit {

   public height: number;

    @ViewChild('commands')
    public commandsGrid: DxDataGridComponent;
    @ViewChild('agentCommandPopup')
    public agentCommandPopup: LocalAgentCommandComponent;
    @ViewChild('agentPicker')
    public agentPicker: DxSelectBoxComponent;

    // Main Data-Grid
    public dataSource: any;

    // List of local agents
    public localAgents: IOcbLocalAgent[] = null;
    public requestedAgentId?: string;
    public selectedLocalAgent: IOcbLocalAgent = null;

    // Date selectors
    public maxDate: Date = now;
    public selectedDates: [Date, Date] = initialDates;

    public isDisplayPopupVisible: boolean = false;

    public isLoadPanelVisible: boolean = true;
    
    public currentEnvironmentName: string = '';
    private currentEnvironmentId: string = '';

    // Labels in JSON object
    private commandsLabels: any;
    private commandsStatusLabels: any;
    private commandsTargetsLabels: any;

    public showGridLoadingPanel = false;

    private getCurrentLocale(): string {
        let currentLocale = 'en-US';
        switch (this.userService.language) {
            case 'en': currentLocale = 'en-US'; break;
            case 'fr': currentLocale = 'fr-FR'; break;
        }
        return currentLocale;
    }

    private getSelectedEndDate() { return this.selectedDates[1].toJSON().slice(0, 10); }

    private getSelectedStartDate() { return this.selectedDates[0].toJSON().slice(0, 10); }

    private handleError(error: any): void {
        let message: string = '';
        if (error instanceof HttpErrorResponse) {
            if (error.error) {
                if (error.error.hasOwnProperty('message')) {
                    message = error.error.message;
                } else if (error.error.hasOwnProperty('name')) {
                    message = error.error.name;
                } else {
                    message = error.error;
                }
            } else {
                message = error.message;
            }
        } else {
            message = error;
        }

        notify(message, 'error', 3000);
    }

    private loadLocalAgents(): void {
        this.localAgentsService.getLocalAgents(this.currentEnvironmentId, false)
            .pipe(finalize(() => this.isLoadPanelVisible = false))
            .subscribe({
                next: (res) => {
                    this.localAgents = res;
                    if (this.requestedAgentId && this.agentPicker) this.agentPicker.value = this.requestedAgentId;
                },
                error: (err) => this.handleError(err)
            });
    }

    constructor(
        private route: ActivatedRoute,
        private userService: UserService,
        private translateService: TranslateService,
        private environmentsService: EnvironmentsService,
        private localAgentsService: LocalAgentsService,
    ) {
        this.displayCommand = this.displayCommand.bind(this);
        this.displayStatus = this.displayStatus.bind(this);
        this.displayCreatedOn = this.displayCreatedOn.bind(this);
        this.displayStartedOn = this.displayStartedOn.bind(this);
        this.displayCompletedOn = this.displayCompletedOn.bind(this);
        this.displayTarget = this.displayTarget.bind(this);
        this.displayName = this.displayName.bind(this);

        this.dataSource = new DataSource<IOcbLocalAgentCommand[], string>({
            key: 'id',
            load: () => new Promise((resolve, reject) => {
                if (!this.selectedLocalAgent?.id) {
                    resolve([]);
                    return;
                };
                this.showGridLoadingPanel = true;
                this.localAgentsService.getCommands(this.selectedLocalAgent.id, false, this.getSelectedStartDate(), this.getSelectedEndDate()).subscribe({
                    next: (data: any) => resolve(data),
                    error: (details: any) => reject(details.message),
                    complete: () => this.showGridLoadingPanel = false
                })
            })
        });
    }

    public ngAfterViewInit() {
        this.route.queryParams.subscribe(queryParams => {
            if (queryParams['agent']) this.requestedAgentId = queryParams['agent'];
        });
        this.route.params.subscribe(_ => {
            this.currentEnvironmentId = this.route.snapshot.params.id;
            this.environmentsService.getEnvironment(this.currentEnvironmentId).subscribe(env => {
                this.currentEnvironmentName = env.name;
            });
            forkJoin([
                this.translateService.get('localAgentsCommandsLabels'),
                this.translateService.get('localAgentsCommandsStatus'),
                this.translateService.get('localAgentsCommandsTargets')
            ]).subscribe({
                next: (labels) => {
                    this.commandsLabels = labels[0];
                    this.commandsStatusLabels = labels[1];
                    this.commandsTargetsLabels = labels[2];
                    this.loadLocalAgents();
                },
                error: (err) => this.handleError(err)
            });
            
        });
    }

    public onContextMenuPreparing(e) {
        if (e.row && e.row.rowType === 'data') {
            e.items = [];
            this.translateService.get('_commons.seeDetails').subscribe(message => {
                e.items = [
                    {
                        text: message,
                        icon: 'clock',
                        onItemClick: (ev) => {
                            this.agentCommandPopup.agentCommand = e.row.data;
                            this.agentCommandPopup.visible = true;
                        }
                    }
                ];
            });
        }
    }

    public onRowClick(e): void {
        this.agentCommandPopup.agentCommand = e.data;
        this.agentCommandPopup.visible = true;
    }

    public filterChanged(e): void {
        if (e) {
            this.selectedLocalAgent = e;
            this.commandsGrid?.instance?.refresh().catch(() => { });
        } else {
            this.dataSource = [];
        }
    }

    public dateChanged(): void { this.commandsGrid?.instance?.refresh().catch(() => { }); }

    public displayCommand(rowData) { return this.commandsLabels[OcbLocalAgentCommand[rowData.command]]; }

    public displayStatus(rowData) { return this.commandsStatusLabels[OcbLocalAgentCommandStatus[rowData.status]]; }

    public displayDate(dateValue): Date { return new Date(dateValue + 'Z'); }
    
    public displayCreatedOn(rowData): string {
        const locale = this.getCurrentLocale();
        return this.displayDate(rowData.createdOn).toLocaleString(locale);
    }

    public displayStartedOn(rowData): string | null {
        if (rowData.startedOn) {
            const locale = this.getCurrentLocale();
            return this.displayDate(rowData.startedOn).toLocaleString(locale);
        }
        else {
            return null;
        }
    }

    public displayCompletedOn(rowData): string | null {
        if (rowData.completedOn) {
            const locale = this.getCurrentLocale();
            return this.displayDate(rowData.completedOn).toLocaleString(locale);
        }
        else {
            return null;
        }
    }

    public displayTarget(rowData): string {
        return this.commandsTargetsLabels[OcbLocalAgentCommandTarget[rowData.target]];
    }

    public displayName(rowData): string {
        return rowData.requestedByUser ? rowData.requestedByUser.displayName : '';
    }

    public refresh() { this.commandsGrid?.instance?.refresh().catch(() => { }); }
}
